import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionState, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * TT: Turn-tilt (draai-kiepraam). Dual mode:
 * - Tilt: bottom-hinged X-axis rotation (small angle)
 * - Turn: side-hinged Y-axis rotation (full swing)
 */
export class TurnTiltSection implements SectionInterface {
  readonly group: THREE.Group;
  private turnPivot: THREE.Group;
  private tiltPivot: THREE.Group;
  private hingeSide: 'left' | 'right';

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-tt-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: 'TT' };

    this.hingeSide = config.orientation.hingeSide ?? 'left';

    // Effective dimensions after insetting from adjacent mullions
    const effW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const clearW = effW - 2 * PROFILE.sashWidth;
    const clearH = effH - 2 * PROFILE.sashWidth;

    // Sash assembly
    const sashAssembly = new THREE.Group();
    sashAssembly.name = 'sash-assembly';

    const sash = createSash(effW, effH, PROFILE.sashWidth, PROFILE.sashDepth, frameMaterial);
    sashAssembly.add(sash);

    const glass = createGlassPane(
      clearW, clearH, PROFILE.glassThickness,
      materialLib.glass(config.infill.tint, config.infill.glazingTier),
    );
    glass.position.set(effW / 2, effH / 2, 0);
    sashAssembly.add(glass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(effW / 2, effH / 2, 0);
      sashAssembly.add(roeden);
    }

    // Handle on opposite side of hinge, relative to effective sash
    const handle = createHandle(materialLib.handleMaterial(config.hardware.handleFinish));
    const handleX = this.hingeSide === 'left'
      ? effW - PROFILE.sashWidth / 2
      : PROFILE.sashWidth / 2;
    handle.position.set(handleX, effH / 2, PROFILE.sashDepth / 2);
    sashAssembly.add(handle);

    // Tilt pivot: at bottom edge of sash, X-axis rotation
    this.tiltPivot = new THREE.Group();
    this.tiltPivot.name = 'tilt-pivot';
    // sashAssembly sits at (0,0) relative to tiltPivot; tiltPivot will be offset by insets
    this.tiltPivot.add(sashAssembly);

    // Turn pivot: at hinge edge (inner face of mullion/frame), Y-axis rotation
    this.turnPivot = new THREE.Group();
    this.turnPivot.name = 'turn-pivot';

    if (this.hingeSide === 'left') {
      this.turnPivot.position.set(insets.left, 0, 0);
      this.tiltPivot.position.set(0, insets.bottom, 0);
    } else {
      this.turnPivot.position.set(insets.left + effW, 0, 0);
      this.tiltPivot.position.set(-effW, insets.bottom, 0);
    }

    this.turnPivot.add(this.tiltPivot);
    this.group.add(this.turnPivot);

    // Apply initial state
    if (config.openState === SectionState.TILT && config.openAmount > 0) {
      this.setTiltAngle(config.openAmount * (Math.PI / 12));
    } else if (config.openState === SectionState.TURN && config.openAmount > 0) {
      this.setOpenAngle(config.openAmount * (Math.PI / 2));
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setOpenAngle(rad: number): void {
    // Turn mode: reset tilt, apply turn
    this.tiltPivot.rotation.x = 0;
    const sign = this.hingeSide === 'left' ? -1 : 1;
    this.turnPivot.rotation.y = sign * rad;
  }

  setTiltAngle(rad: number): void {
    // Tilt mode: reset turn, apply tilt (inward = positive X rotation at bottom)
    this.turnPivot.rotation.y = 0;
    this.tiltPivot.rotation.x = rad;
  }
}
